<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Eficiencia de Automacao',
  },
});

const bot = computed(() => props.data?.bot || {});
const autoAssignment = computed(() => props.data?.auto_assignment || {});
const cannedResponses = computed(() => props.data?.canned_responses || {});
const automationRules = computed(() => props.data?.automation_rules || {});

function getScoreColor(score) {
  if (score >= 50) return 'text-n-teal-11';
  if (score >= 30) return 'text-n-blue-11';
  if (score >= 15) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-cpu text-lg text-n-cyan-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-n-slate-11">Score:</span>
        <span
          class="text-xl font-bold"
          :class="getScoreColor(data.efficiency_score)"
        >
          {{ data.efficiency_score || 0 }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Automation Rate -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm text-n-slate-12">Taxa de Automacao</span>
          <span class="text-sm font-bold text-n-slate-12">{{ data.automation_rate || 0 }}%</span>
        </div>
        <div class="h-4 bg-n-alpha-2 rounded-full overflow-hidden">
          <div
            class="h-full bg-n-cyan-9 rounded-full transition-all duration-300"
            :style="{ width: `${data.automation_rate || 0}%` }"
          />
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>{{ data.automated_conversations || 0 }} automatizadas</span>
          <span>{{ data.total_conversations || 0 }} total</span>
        </div>
      </div>

      <!-- Bot Performance -->
      <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-alpha-1">
        <div class="flex items-center gap-2">
          <span class="i-lucide-bot text-n-violet-10" />
          <span class="text-sm font-medium text-n-slate-12">Bots</span>
        </div>
        <div class="grid grid-cols-3 gap-2 text-center">
          <div>
            <div class="text-lg font-bold text-n-slate-12">
              {{ bot.handled || 0 }}
            </div>
            <div class="text-xs text-n-slate-10">Atendidas</div>
          </div>
          <div>
            <div class="text-lg font-bold text-n-teal-11">
              {{ bot.resolved || 0 }}
            </div>
            <div class="text-xs text-n-slate-10">Resolvidas</div>
          </div>
          <div>
            <div
              class="text-lg font-bold"
              :class="getScoreColor(bot.resolution_rate)"
            >
              {{ bot.resolution_rate || 0 }}%
            </div>
            <div class="text-xs text-n-slate-10">Taxa</div>
          </div>
        </div>
        <div class="flex h-2 rounded-full overflow-hidden bg-n-alpha-2">
          <div
            class="bg-n-teal-9"
            :style="{ width: `${bot.resolution_rate || 0}%` }"
          />
          <div
            class="bg-n-amber-9"
            :style="{ width: `${bot.handoff_rate || 0}%` }"
          />
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>Resolvidas: {{ bot.resolution_rate || 0 }}%</span>
          <span>Handoffs: {{ bot.handoff_rate || 0 }}%</span>
        </div>
      </div>

      <!-- Auto Assignment -->
      <div
        class="flex items-center justify-between p-3 rounded-lg bg-n-alpha-1"
      >
        <div class="flex items-center gap-2">
          <span class="i-lucide-git-branch text-n-blue-10" />
          <span class="text-sm text-n-slate-12">Auto-atribuicao</span>
        </div>
        <div class="text-right">
          <div class="text-sm font-bold text-n-slate-12">
            {{ autoAssignment.enabled_inboxes || 0 }}/{{
              autoAssignment.total_inboxes || 0
            }}
          </div>
          <div class="text-xs text-n-slate-10">canais habilitados</div>
        </div>
      </div>

      <!-- Resources -->
      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <div class="flex items-center gap-1">
            <span class="i-lucide-file-text text-n-slate-10" />
            <span class="text-lg font-bold text-n-slate-12">{{
              cannedResponses.total_available || 0
            }}</span>
          </div>
          <span class="text-xs text-n-slate-11">Respostas Prontas</span>
        </div>
        <div class="flex flex-col items-center p-2 rounded-lg bg-n-alpha-1">
          <div class="flex items-center gap-1">
            <span class="i-lucide-workflow text-n-slate-10" />
            <span class="text-lg font-bold text-n-slate-12">{{ automationRules.active || 0 }}/{{
                automationRules.total || 0
              }}</span>
          </div>
          <span class="text-xs text-n-slate-11">Regras Automacao</span>
        </div>
      </div>
    </div>
  </div>
</template>
